/**
 * Renders a compact SVG forgetting curve for a single item's review history.
 *
 * Each review resets the curve to R=1.0; the curve decays until the next
 * review (or `horizonDays` into the future from the last review).
 *
 * Green dots = correct reviews, red dots = wrong reviews.
 */

import { type ReactNode } from "react";
import { forgettingCurvePoints, retrievability, type ReviewPoint } from "@music-app/learning-system";

export interface ForgettingCurveProps {
  reviews: ReviewPoint[];
  horizonDays?: number;
  width?: number;
  height?: number;
}

export function ForgettingCurve({
  reviews,
  horizonDays = 30,
  width = 200,
  height = 56,
}: ForgettingCurveProps): ReactNode {
  if (reviews.length === 0) return null;

  const sorted = [...reviews].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  const originMs = new Date(sorted[0]!.timestamp).getTime();
  const lastMs = new Date(sorted[sorted.length - 1]!.timestamp).getTime();
  const totalDays = (lastMs - originMs) / 86400000 + horizonDays;
  if (totalDays <= 0) return null;

  const curvePoints = forgettingCurvePoints(reviews, horizonDays);
  if (curvePoints.length === 0) return null;

  const pad = { l: 2, r: 4, t: 4, b: 14 };
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;

  const toX = (dayOffset: number) => pad.l + (dayOffset / totalDays) * plotW;
  const toY = (r: number) => pad.t + (1 - Math.max(0, Math.min(1, r))) * plotH;

  // Curve polyline — split at segment resets (where R=1.0 begins a new segment)
  // Each review starts a new segment; break the polyline there.
  const segments: string[] = [];
  let current: string[] = [];
  for (const p of curvePoints) {
    if (p.retrievability >= 0.999 && current.length > 0) {
      segments.push(current.join(" "));
      current = [];
    }
    current.push(`${toX(p.dayOffset).toFixed(1)},${toY(p.retrievability).toFixed(1)}`);
  }
  if (current.length > 0) segments.push(current.join(" "));

  // Review dots: R at the moment of each review (end of prior decay segment)
  const dotData = sorted.map((r, i) => {
    const dayOffset = (new Date(r.timestamp).getTime() - originMs) / 86400000;
    let rAtMoment: number;
    if (i === 0) {
      rAtMoment = 1.0;
    } else {
      const prev = sorted[i - 1]!;
      const elapsed = (new Date(r.timestamp).getTime() - new Date(prev.timestamp).getTime()) / 86400000;
      rAtMoment = retrievability(prev.stability, elapsed);
    }
    return { dayOffset, rAtMoment, correct: r.correct };
  });

  return (
    <svg
      width={width}
      height={height}
      aria-label="Forgetting curve"
      className="block text-ink"
      role="img"
    >
      {/* Baseline axis */}
      <line
        x1={pad.l} y1={toY(0)}
        x2={pad.l + plotW} y2={toY(0)}
        stroke="currentColor" strokeOpacity={0.12} strokeWidth={0.5}
      />
      {/* 90% retention guideline */}
      <line
        x1={pad.l} y1={toY(0.9)}
        x2={pad.l + plotW} y2={toY(0.9)}
        stroke="currentColor" strokeOpacity={0.1} strokeWidth={0.5}
        strokeDasharray="2 2"
      />
      {/* Decay curve segments */}
      {segments.map((pts, i) => (
        <polyline
          key={i}
          points={pts}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.45}
          strokeWidth={1}
        />
      ))}
      {/* Review dots */}
      {dotData.map((d, i) => (
        <circle
          key={i}
          cx={toX(d.dayOffset)}
          cy={toY(d.rAtMoment)}
          r={2.5}
          fill={d.correct ? "#22c55e" : "#ef4444"}
          fillOpacity={0.85}
          aria-label={d.correct ? "correct review" : "wrong review"}
        />
      ))}
      {/* "now" divider between history and future tail */}
      <line
        x1={toX((lastMs - originMs) / 86400000)}
        y1={pad.t}
        x2={toX((lastMs - originMs) / 86400000)}
        y2={pad.t + plotH}
        stroke="currentColor" strokeOpacity={0.15} strokeWidth={0.5}
        strokeDasharray="2 2"
      />
      {/* Horizon label */}
      <text
        x={pad.l + plotW}
        y={pad.t + plotH + 10}
        fontSize={7}
        fill="currentColor"
        fillOpacity={0.4}
        textAnchor="end"
      >
        +{horizonDays}d
      </text>
    </svg>
  );
}
